import { datadogRum } from '@datadog/browser-rum';
import DataPointsLib from '@retracedgmbh/data-points-lib';

const dataPointsLib = new DataPointsLib({});
const lib = dataPointsLib.initializeDataPoints();

// Track which problematic data point ids we've already reported so a
// re-rendering page (getDataPointById runs per question header) doesn't spam
// Datadog/console with the same issue.
const reportedDataPointIssues = new Set();

const reportDataPointIssue = (id, error) => {
  if (reportedDataPointIssues.has(id)) return;
  reportedDataPointIssues.add(id);
  console.error(
    '[DataPointsLIB] data point issue for id:',
    JSON.stringify(id),
    error,
  );
  datadogRum.addError(error, {
    source: 'getDataPointByIdSafe',
    dataPointId: id,
  });
};

/**
 * Resilient replacement for the lib's getDataPointById.
 *
 * The underlying lib throws when an id is not found, which crashes whole pages
 * (e.g. the questionnaire answer page, where it runs for every question header).
 * Some questions carry malformed ids — most commonly trailing whitespace like
 * "number_of_employees   " — so we trim to recover and never throw. Any genuine
 * mismatch (unknown/renamed/removed datapoint) or whitespace corruption is
 * reported to Datadog (queryable by dataPointId / source) for visibility.
 *
 * @param {string} id
 * @returns the data point definition, or null when it can't be resolved.
 */
export const getDataPointByIdSafe = id => {
  if (!id) return null;
  const trimmed = id.trim();
  if (trimmed !== id) {
    reportDataPointIssue(
      id,
      new Error(
        `[DataPointsLIB] data point id has surrounding whitespace: ${JSON.stringify(id)}`,
      ),
    );
  }
  try {
    return lib.getDataPointById(trimmed);
  } catch (error) {
    reportDataPointIssue(id, error);
    return null;
  }
};

// Expose the lib's methods, but harden getDataPointById so no consumer can crash
// the UI on a malformed/unknown data point id.
const dataPoints = {
  ...lib,
  getDataPointById: getDataPointByIdSafe,
};

export default dataPoints;
